import React, { useCallback, useEffect, useMemo, useState } from "react";

import withChallenge from "HOCs/withChallenge";
import {
  questionsPointsWorth,
  addOption,
  addQuestion,
  option,
  selectAnswer,
  writeYourQuestion,
  alternativesMsg,
  cancel,
  launchQuestion,
  done,
  provideAnStatement,
  selectAnAnswer,
  questionLaunchedMessage,
} from "./messages";
import { useChallenge } from "contexts/Challenge/useChallenge";
import { Input, TextArea } from "components/Form";
import { ReactComponent as CheckIcon } from "assets/icons/check.svg";
import { ReactComponent as XIcon } from "assets/icons/icon-x.svg";
import { NumberInput } from "molecules";
import { Link } from "react-router-dom";
import Button from "components/Button";
import classNames from "classnames";
import Wrapper from "./Wrapper";
import { useSnackbarNotification } from "components/SnackbarNotifications";
import { sendErrorEmail } from "utils";
import { loading } from "components/SocialLoginButtons/messages";
import { useHistory } from "react-router-dom";

const AddQuestion = () => {
  const { viewingChallenge, request: challenge } = useChallenge();
  const [alternatives, setAlternatives] = useState([`${option} ${1}`]);
  const [isSelectingAnswer, setIsSelectingAnswer] = useState(false);
  const [answer, setAnswer] = useState();
  const [statement, setStatement] = useState();
  const [errors, setErrors] = useState({});
  const [points, setPoints] = useState(3);
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();

  const { openGeneralErrorSnackbar, openCustomSnackbar } =
    useSnackbarNotification();

  useEffect(() => {
    errors.answer &&
      setTimeout(() => setErrors({ ...errors, answer: null }), 1000);
  }, [errors]);

  const onAlternativeChange = (newValue, index) => {
    const newAlternatives = [...alternatives];
    newAlternatives[index] = newValue;
    setAlternatives(newAlternatives);
  };

  const addAlternative = useCallback(() => {
    setAlternatives([...alternatives, `${option} ${alternatives.length + 1}`]);
  }, [alternatives]);

  const removeAlternative = useCallback(
    (toRemove) => {
      setAlternatives(
        alternatives.filter((alternative) => alternative !== toRemove)
      );
    },
    [alternatives]
  );

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        addAlternative();
      }
    },
    [addAlternative]
  );

  const backTo = useMemo(() => {
    return `/challenge/${viewingChallenge}/questions`;
  }, [viewingChallenge]);

  const onSelectAlternative = useCallback(
    (alternative) => {
      isSelectingAnswer && setAnswer(alternative);
    },
    [isSelectingAnswer]
  );

  const onRemoveAlternative = useCallback(
    (alternative) => {
      if (alternative === answer) setAnswer(null);
      removeAlternative(alternative);
    },
    [answer, removeAlternative]
  );

  const onStatementChange = useCallback(
    ({ target: { value } }) => {
      setStatement(value);
      setErrors({ ...errors, statement: null });
    },
    [errors]
  );

  const allFieldsAreValid = useCallback(() => {
    let errors = {};

    if (!answer) errors = { ...errors, answer: selectAnAnswer };

    if (!statement) errors = { ...errors, statement: provideAnStatement };

    setErrors(errors);

    if (!answer || !statement) return false;

    return true;
  }, [answer, statement]);

  const onSubmit = useCallback(async () => {
    if (!allFieldsAreValid()) return;

    try {
      setIsLoading(true);

      await challenge.post("questions/add", {
        statement,
        alternatives,
        answer,
        points,
      });

      history.push(`/challenge/${viewingChallenge}/questions`);

      openCustomSnackbar({
        variant: "success",
        message: questionLaunchedMessage,
        delay: 10000,
      });
    } catch (error) {
      console.error(error);
      sendErrorEmail(error, "on adding question");
      openGeneralErrorSnackbar();
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allFieldsAreValid,
    alternatives,
    answer,
    challenge,
    points,
    statement,
    viewingChallenge,
  ]);

  return (
    <Wrapper cardTitle={addQuestion}>
      <div className="add-question form">
        <div className="add-question__title">
          <TextArea
            placeholder={writeYourQuestion}
            rows={2}
            onChange={onStatementChange}
            error={errors?.statement}
            autoFocus={true}
          />
        </div>
        <div className="add-question__alternatives-title">
          {isSelectingAnswer ? selectAnswer : alternativesMsg}
        </div>
        <div className="add-question__alternatives">
          {alternatives.map((alternative, index) => (
            <div
              className={classNames("add-question__alternative", {
                isSelectingAnswer,
                active: answer === alternative,
              })}
              key={index}
              onClick={() => onSelectAlternative(alternative)}
            >
              <div className={"add-question__alternative-input"}>
                {isSelectingAnswer ? (
                  <p>{alternative}</p>
                ) : (
                  <Input
                    defaultValue={`${alternative} ${
                      alternative === option ? index + 1 : ""
                    }`}
                    onChange={({ target: { value } }) =>
                      onAlternativeChange(value, index)
                    }
                    onKeyDown={onKeyDown}
                    autoFocus={
                      index === alternatives.length - 1 &&
                      alternatives.length > 1
                    }
                  />
                )}
              </div>
              {alternatives.length > 1 && !isSelectingAnswer && (
                <XIcon
                  width={16}
                  height={16}
                  onClick={() => onRemoveAlternative(alternative)}
                />
              )}
            </div>
          ))}
          {!isSelectingAnswer && (
            <div className={"add-question__alternatives-add"}>
              <button onClick={addAlternative}>{addOption}</button>
            </div>
          )}
        </div>
        {isSelectingAnswer ? (
          <div className="add-question__selecting-alternative">
            <Button
              size="adapted"
              className="button--primary "
              variation="square-rounded"
              theme="primary"
              type="submit"
              onClick={() => setIsSelectingAnswer(false)}
            >
              {done}
            </Button>
          </div>
        ) : (
          <div
            onClick={() => setIsSelectingAnswer(true)}
            className={classNames("add-question__select-alternative", {
              error: !!errors?.answer,
            })}
          >
            <CheckIcon />
            <p>{errors.answer ? errors.answer : selectAnswer}</p>
          </div>
        )}
        {!isSelectingAnswer && (
          <div className="add-question__points">
            <p>{questionsPointsWorth}</p>
            <NumberInput onValueChange={setPoints} defaultValue={points} />
          </div>
        )}
        {!isSelectingAnswer && (
          <div className="add-question__controls">
            <Link to={backTo} className="new-challenge__controls-jump">
              {cancel}
            </Link>
            <Button
              size="medium"
              className="button--primary "
              variation="square-rounded"
              theme="primary"
              type="submit"
              disabled={isLoading}
              onClick={onSubmit}
            >
              {isLoading ? loading : launchQuestion}
            </Button>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default withChallenge(AddQuestion);
